// /web/src/pages/calculators/Cycles.jsx
import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useApi } from "../../lib/api";
import { trackCalculatorRun } from "../../lib/amplitude";
import CalculatorLayout, {
  CalculatorSection,
} from "../../components/calculators/CalculatorLayout.jsx";
import useUsageEvent from "../../hooks/useUsageEvent";
import DownloadCycleReportBtn from "../../components/pdf/DownloadCycleReportBtn.jsx";

const CYCLE_CALCULATOR_NOTES = [
  "Long-stay provision follows London Plan or local guidance first, with BCO occupancy ratios (1 space per 10 people) as the fallback.",
  "Short-stay visitor parking is only shown where the authority sets a threshold or PTAL trigger.",
  "Provide both GEA and GIA when possible. We also accept NIA to infer staff numbers and flag accessible or cargo/adapted allowances.",
];

function toErrorMessage(err) {
  if (!err) return "Unknown error";
  if (typeof err === "string") return err;
  if (err.detail) {
    if (typeof err.detail === "string") return err.detail;
    if (Array.isArray(err.detail) && err.detail[0]?.msg) return err.detail[0].msg;
  }
  if (typeof err.message === "string" && err.message !== "[object Object]") return err.message;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

export default function CyclesCalc() {
  const api = useApi();
  const params = useParams();
  const [sp] = useSearchParams();
  const projectId =
    params.id ||
    sp.get("project_id") ||
    sp.get("projectId") ||
    sp.get("project") ||
    null;
  const rawUsageEventId = sp.get("usage_event_id");
  const { event: historyEvent, loading: historyLoading, error: historyError, usageEventId } =
    useUsageEvent(rawUsageEventId);

  const [project, setProject] = React.useState(null);
  const [inputs, setInputs] = React.useState([]);
  const [gia, setGia] = React.useState(""); // auto-filled from inputs sum
  const [gea, setGea] = React.useState("");
  const [nia, setNia] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [projectLoading, setProjectLoading] = React.useState(!!projectId);
  const [loadError, setLoadError] = React.useState("");
  const [projects, setProjects] = React.useState([]);
  const [loadingProjects, setLoadingProjects] = React.useState(!projectId);
  const [pickErr, setPickErr] = React.useState("");

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, []);

  const areaMetric = result?.inputs?.area_metric || "gia";
  const areaLabel = areaMetric === "gea" ? "Assessed GEA" : "Assessed GIA";
  const secondaryAreaRaw = areaMetric === "gea" ? result?.inputs?.gia_m2 : result?.inputs?.gea_m2;
  const secondaryAreaNumber = Number(secondaryAreaRaw);
  const secondaryAreaHasValue =
    secondaryAreaRaw !== undefined &&
    secondaryAreaRaw !== null &&
    !Number.isNaN(secondaryAreaNumber) &&
    secondaryAreaNumber > 0;
  const secondaryAreaValue = secondaryAreaHasValue ? secondaryAreaRaw : null;
  const secondaryAreaLabel = secondaryAreaValue
    ? areaMetric === "gea"
      ? "Provided GIA"
      : "Provided GEA"
    : null;
  const hasShortStayRequirement = Boolean(result?.standard_applied?.short_stay_basis);
  const projectSummary = project
    ? [
        project.name,
        project.postcode,
        project.local_authority,
        project.floors_count ? `${project.floors_count} floors` : null,
      ].filter(Boolean)
    : null;

  const getRowGia = (row) => {
    if (!row || typeof row !== "object") return 0;
    const candidates = [row.gia_m2, row.GIA, row.gia, row.giaM2, row.giaSqm, row.area_gia_m2];
    for (const v of candidates) {
      const n = Number(v);
      if (!Number.isNaN(n) && n > 0) return n;
    }
    return 0;
  };
  const getRowGea = (row) => {
    if (!row || typeof row !== "object") return 0;
    const candidates = [row.gea_m2, row.GEA, row.gea, row.geaM2, row.geaSqm, row.area_gea_m2];
    for (const v of candidates) {
      const n = Number(v);
      if (!Number.isNaN(n) && n > 0) return n;
    }
    return 0;
  };
  const getRowNia = (row) => {
    if (!row || typeof row !== "object") return 0;
    const candidates = [row.nia_m2, row.NIA, row.nia, row.niaM2, row.niaSqm, row.area_nia_m2];
    for (const v of candidates) {
      const n = Number(v);
      if (!Number.isNaN(n) && n > 0) return n;
    }
    return 0;
  };
  const computeTotalGia = React.useCallback(
    (rows) => (Array.isArray(rows) ? rows.reduce((sum, r) => sum + getRowGia(r), 0) : 0),
    []
  );
  const computeTotalGea = React.useCallback(
    (rows) => (Array.isArray(rows) ? rows.reduce((sum, r) => sum + getRowGea(r), 0) : 0),
    []
  );
  const computeTotalNia = React.useCallback(
    (rows) => (Array.isArray(rows) ? rows.reduce((sum, r) => sum + getRowNia(r), 0) : 0),
    []
  );
  const describeStandard = React.useCallback((basis, perM2, personsPerSpace, perPerson) => {
    if (!basis) return null;
    if (basis === "per_m2") {
      const val = Number(perM2);
      if (!Number.isFinite(val) || val <= 0) return null;
      return `1 per ${val.toLocaleString()} m²`;
    }
    if (basis === "persons_per_space") {
      const val = Number(personsPerSpace);
      if (!Number.isFinite(val) || val <= 0) return null;
      return `1 per ${val.toLocaleString()} people`;
    }
    if (basis === "per_person") {
      const val = Number(perPerson);
      if (!Number.isFinite(val) || val <= 0) return null;
      const digits = val >= 1 ? 1 : 2;
      return `${val.toLocaleString(undefined, { maximumFractionDigits: digits })} spaces per person`;
    }
    return null;
  }, []);
  const standardApplied = result?.standard_applied || null;
  const longStandardText = React.useMemo(() => {
    if (!standardApplied) return null;
    const basis = standardApplied.long_stay_basis || (standardApplied.long_stay_per_m2 ? "per_m2" : null);
    return describeStandard(
      basis,
      standardApplied.long_stay_per_m2,
      standardApplied.long_stay_persons_per_space,
      standardApplied.long_stay_per_person
    );
  }, [standardApplied, describeStandard]);
  const shortStandardText = React.useMemo(() => {
    if (!standardApplied) return null;
    const basis = standardApplied.short_stay_basis || (standardApplied.short_stay_per_m2 ? "per_m2" : null);
    return describeStandard(
      basis,
      standardApplied.short_stay_per_m2,
      standardApplied.short_stay_persons_per_space,
      standardApplied.short_stay_per_person
    );
  }, [standardApplied, describeStandard]);
  const standardSummary = React.useMemo(() => {
    const bits = [];
    if (longStandardText) bits.push(`${longStandardText} (long-stay)`);
    if (shortStandardText) bits.push(`${shortStandardText} (short-stay)`);
    if (bits.length === 0) return "—";
    return bits.join(", ");
  }, [longStandardText, shortStandardText]);
  const occupancyRatioNumber = Number(result?.inputs?.occupancy_ratio_sqm_per_person);
  const occupancyRatioDisplay =
    Number.isFinite(occupancyRatioNumber) && occupancyRatioNumber > 0
      ? `${occupancyRatioNumber.toLocaleString(undefined, {
          maximumFractionDigits: occupancyRatioNumber >= 10 ? 1 : 2,
        })} m²/person`
      : null;
  const estimatedPersonsNumber = Number(result?.inputs?.estimated_persons);
  const estimatedPersonsDisplay = Number.isFinite(estimatedPersonsNumber)
    ? estimatedPersonsNumber.toLocaleString()
    : null;
  const niaResultNumber = Number(result?.inputs?.nia_m2);

  React.useEffect(() => {
    let cancelled = false;

    async function loadProjectAndInputs(pid) {
      try {
        setProjectLoading(true);
        setLoadError("");
        setResult(null);
        setGia("");
        setGea("");
        setNia("");
        setInputs([]);
        const [p, floorInputs] = await Promise.all([
          api(`/projects/${pid}`),
          api(`/projects/${pid}/inputs`).catch(() => []),
        ]);
        if (cancelled) return;

        setProject(p || null);
        const rows = Array.isArray(floorInputs) ? floorInputs : floorInputs?.items || [];
        setInputs(rows);

        const giaTotal = (() => {
          if (typeof p?.total_gia_m2 === "number" && p.total_gia_m2 > 0) {
            return Math.round(p.total_gia_m2);
          }
          return Math.round(computeTotalGia(rows));
        })();
        const geaTotal = (() => {
          if (typeof p?.total_gea_m2 === "number" && p.total_gea_m2 > 0) {
            return Math.round(p.total_gea_m2);
          }
          return Math.round(computeTotalGea(rows));
        })();
        const niaTotal = (() => {
          if (typeof p?.total_nia_m2 === "number" && p.total_nia_m2 > 0) {
            return Math.round(p.total_nia_m2);
          }
          return Math.round(computeTotalNia(rows));
        })();

        if (giaTotal > 0) {
          setGia(String(giaTotal));
        }
        if (geaTotal > 0) {
          setGea(String(geaTotal));
        }
        if (niaTotal > 0) {
          setNia(String(niaTotal));
        }
      } catch (e) {
        if (cancelled) return;
        console.error(e);
        setProject(null);
        setInputs([]);
        setGia("");
        setGea("");
        setNia("");
        const message = toErrorMessage(e) || "Could not load project or inputs.";
        setLoadError(message);
        toast.error(message);
      } finally {
        if (!cancelled) setProjectLoading(false);
      }
    }

    async function loadProjectsList() {
      try {
        setLoadingProjects(true);
        setPickErr("");
        const list = await api(`/projects`);
        if (cancelled) return;
        setProjects(Array.isArray(list) ? list : []);
      } catch (e) {
        if (cancelled) return;
        setPickErr(toErrorMessage(e) || "Failed to load projects");
      } finally {
        if (!cancelled) setLoadingProjects(false);
      }
    }

    if (projectId) {
      setProjects([]);
      setLoadingProjects(false);
      setPickErr("");
      loadProjectAndInputs(projectId);
    } else if (!usageEventId) {
      setProject(null);
      setInputs([]);
      setGia("");
      setGea("");
      setNia("");
      setResult(null);
      setLoadError("");
      setProjectLoading(false);
      loadProjectsList();
    } else {
      setProject(null);
      setInputs([]);
      setGia("");
      setGea("");
      setNia("");
      setResult(null);
      setLoadError("");
      setProjectLoading(false);
      setLoadingProjects(false);
      setProjects([]);
      setPickErr("");
    }

    return () => {
      cancelled = true;
    };
  }, [projectId, usageEventId, api, computeTotalGia, computeTotalGea, computeTotalNia]);

  React.useEffect(() => {
    if (!usageEventId || !historyEvent) return;
    const details = historyEvent.details || {};
    if (details.gia_m2 != null) setGia(String(details.gia_m2));
    if (details.gea_m2 != null) setGea(String(details.gea_m2));
    if (details.nia_m2 != null) setNia(String(details.nia_m2));
    if (historyEvent.result_json) {
      setResult(historyEvent.result_json);
    }
  }, [usageEventId, historyEvent]);

  async function runCalc() {
    const giaVal = Number(gia);
    const geaVal = Number(gea);
    const niaVal = Number(nia);
    if (!projectId) {
      toast.error("Please open from a project or set a project_id.");
      return;
    }
    if (!giaVal || giaVal <= 0) {
      toast.error("Total GIA must be greater than zero.");
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const body = { project_id: projectId, gia_m2: giaVal };
      if (Number.isFinite(geaVal) && geaVal > 0) {
        body.gea_m2 = geaVal;
      }
      if (Number.isFinite(niaVal) && niaVal > 0) {
        body.nia_m2 = niaVal;
      }
      const data = await api("/calc/cycles/run", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const longStay = Number(data?.requirements?.long_stay_spaces);
      const shortStay = Number(data?.requirements?.short_stay_spaces);
      trackCalculatorRun("cycles", {
        projectId,
        inputs: body,
        resultSummary: {
          long_stay_spaces: Number.isFinite(longStay) ? longStay : undefined,
          short_stay_spaces: Number.isFinite(shortStay) ? shortStay : undefined,
          area_metric: data?.standard_applied?.area_metric,
          ptal: data?.standard_applied?.ptal?.label || data?.standard_applied?.ptal?.ptal,
        },
      });
      setResult(data);
      toast.success("Calculation complete");
    } catch (err) {
      console.error(err);
      const detail =
        err?.response?.data?.detail ||
        err?.data?.detail ||
        err?.message ||
        "Something went wrong";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  }

  function refreshFromInputs() {
    const totalGia = Math.round(computeTotalGia(inputs));
    const totalGea = Math.round(computeTotalGea(inputs));
    const totalNia = Math.round(computeTotalNia(inputs));
    const updated = [];
    if (totalGia > 0) {
      setGia(String(totalGia));
      updated.push(`GIA ${totalGia.toLocaleString()} m²`);
    }
    if (totalGea > 0) {
      setGea(String(totalGea));
      updated.push(`GEA ${totalGea.toLocaleString()} m²`);
    }
    if (totalNia > 0) {
      setNia(String(totalNia));
      updated.push(`NIA ${totalNia.toLocaleString()} m²`);
    }
    if (updated.length > 0) {
      toast.success(`Updated ${updated.join(" and ")} from floor inputs.`);
    } else {
      toast("No GIA, GEA or NIA values found in floor inputs");
    }
  }

  const viewingHistory = Boolean(usageEventId);
  if (!projectId && !viewingHistory) {
    return (
      <CalculatorLayout
        title="Cycle parking calculator"
        subtitle="Pick a project to run the cycle parking calculator."
        project={null}
        costLabel="Included with subscription"
        onRun={null}
        runLabel="Run calculation"
        runDisabled
      >
        <CalculatorSection
          title="Select a project"
          description="Cycle parking sits within each project. Pick one below to continue."
        >
          {pickErr && (
            <div className="pd-inline-alert pd-inline-alert--error">
              <strong>Error:</strong> {pickErr}
            </div>
          )}
          {loadingProjects ? (
            <div className="pd-inline-placeholder">Loading your projects…</div>
          ) : projects.length === 0 ? (
            <div className="pd-empty-state">
              <p>You don’t have any projects yet.</p>
              <div className="pd-empty-state__actions">
                <Link to="/projects/new" className="pd-btn pd-btn--primary">
                  Create new project
                </Link>
              </div>
            </div>
          ) : (
            <div className="pd-project-grid">
              {projects.map((p) => (
                <Link
                  key={p.id}
                  to={`/calculators/cycles?project_id=${encodeURIComponent(p.id)}`}
                  className="pd-project-card"
                >
                  <div className="pd-project-card__head">
                    <div>
                      <div className="pd-project-card__title">{p.name}</div>
                      <div className="pd-project-card__meta">
                        {p.postcode || "—"} · {p.local_authority || "—"}
                      </div>
                    </div>
                    <span className="pd-pill">{(p.building_use || "office").toUpperCase()}</span>
                  </div>
                  <div className="pd-project-card__meta">Floors: {p.floors_count ?? "—"}</div>
                  <div className="pd-project-card__foot">
                    <span>Project ID: {p.id}</span>
                    <span className="pd-project-card__cta">Open cycle calculator →</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CalculatorSection>
      </CalculatorLayout>
    );
  }

  const historyProject = historyEvent?.project_id
    ? { id: historyEvent.project_id, name: historyEvent.project_name }
    : null;
  const layoutProject = project || historyProject;
  const historySummary = historyEvent
    ? [
        historyEvent.project_name || "Saved run",
        historyEvent.created_at
          ? new Date(historyEvent.created_at).toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : null,
      ].filter(Boolean)
    : null;
  const layoutDetails = projectId ? projectSummary : historySummary;
  const bannerText = (() => {
    if (!usageEventId) return null;
    if (historyLoading) return "Loading saved calculator run…";
    if (historyError) return `Failed to load saved run: ${historyError}`;
    if (historyEvent) {
      const ts = historyEvent.created_at
        ? new Date(historyEvent.created_at).toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
          })
        : null;
      return ts
        ? `Restored calculator inputs from ${ts}. Run again to refresh numbers.`
        : "Restored calculator inputs from your history.";
    }
    return null;
  })();

  return (
    <CalculatorLayout
      title="Cycle Parking Provision"
      subtitle="Planner-grade split of long- and short-stay cycle parking provision."
      project={layoutProject}
      projectDetails={layoutDetails}
      costLabel="Included with subscription"
      onRun={projectId ? runCalc : null}
      runLabel="Run calculation"
      runDisabled={loading || !projectId || projectLoading}
      runLoading={loading}
    >
      {usageEventId && bannerText && (
        <div className="pd-inline-alert pd-inline-alert--info">{bannerText}</div>
      )}

      {loadError && (
        <div className="pd-inline-alert pd-inline-alert--error">
          <strong>Error:</strong> {loadError}
        </div>
      )}

      <CalculatorSection
        title="Input parameters"
        description="Provide headline areas to estimate long- and short-stay cycle parking."
        actions={
          <button
            type="button"
            className="pd-btn pd-btn--ghost"
            onClick={refreshFromInputs}
            disabled={projectLoading || loading}
          >
            Refresh areas from floors
          </button>
        }
      >
        <div className="pd-input-grid pd-input-grid--triple">
          <div className="pd-form-group pd-form-group--full">
            <label>Project</label>
            <input type="text" value={projectLoading ? "Loading…" : project?.name || "—"} readOnly />
            <p className="pd-form-note">
              {project?.local_authority ? `Local authority: ${project.local_authority}` : "Project name shown above."}
            </p>
          </div>

          <div className="pd-form-group">
            <label>Total GIA (m²)</label>
            <input
              type="number"
              min="1"
              step="1"
              value={gia}
              onChange={(e) => setGia(e.target.value)}
              placeholder="Auto-summed from floors"
            />
            <p className="pd-form-note">Auto-filled from your floor-by-floor GIA. Override before running if needed.</p>
          </div>

          <div className="pd-form-group">
            <label>Total GEA (m²)</label>
            <input
              type="number"
              min="0"
              step="1"
              value={gea}
              onChange={(e) => setGea(e.target.value)}
              placeholder="Auto-summed from floors"
            />
            <p className="pd-form-note">Provide when available — London Plan calculations use GEA instead of GIA.</p>
          </div>

          <div className="pd-form-group">
            <label>Total NIA (m²)</label>
            <input
              type="number"
              min="0"
              step="1"
              value={nia}
              onChange={(e) => setNia(e.target.value)}
              placeholder="Auto-summed from floors"
            />
            <p className="pd-form-note">Needed for BCO fallback. We infer staff numbers at 1 space per 10 people.</p>
          </div>
        </div>
      </CalculatorSection>

      <CalculatorSection title="Notes" description="How we size cycle parking before you run the model.">
        <div className="pd-calculator__notes">
          <ul>
            {CYCLE_CALCULATOR_NOTES.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      </CalculatorSection>

      <CalculatorSection
        title="Results"
        description="Outputs split by London Plan vs. BCO requirements, plus explanatory notes."
        actions={
          <DownloadCycleReportBtn
            project={project}
            result={result}
            disabled={loading || projectLoading || historyLoading || !result}
          />
        }
      >
        {result ? (
          <>
            <div className="pd-results-card">
              <div className="pd-section-head">
                <div>
                  <h4 className="pd-h4">Cycle parking provision</h4>
                  {(result?.authority?.name || result?.authority?.code) && (
                    <p className="pd-results-card__meta">
                      Authority: {result?.authority?.name || result?.authority?.code}
                      {result?.authority?.name && result?.authority?.code ? ` · ${result.authority.code}` : ""}
                    </p>
                  )}
                </div>
              </div>

              <div className="pd-stat-grid">
                <Stat label="Long-stay cycle spaces" value={result?.requirements?.long_stay_spaces} />
                {hasShortStayRequirement && (
                  <Stat label="Short-stay cycle spaces" value={result?.requirements?.short_stay_spaces} />
                )}
                <Stat label={areaLabel} value={formatArea(result?.inputs?.area_used_m2)} />
                {secondaryAreaLabel && <Stat label={secondaryAreaLabel} value={formatArea(secondaryAreaValue)} />}
                {Number.isFinite(niaResultNumber) && niaResultNumber > 0 && (
                  <Stat label="Assessed NIA" value={formatArea(result?.inputs?.nia_m2)} />
                )}
                {occupancyRatioDisplay && <Stat label="Occupancy ratio" value={occupancyRatioDisplay} />}
                {estimatedPersonsDisplay && <Stat label="Estimated staff" value={estimatedPersonsDisplay} />}
              </div>

              {Array.isArray(result?.notes) && result.notes.length > 0 && (
                <div className="pd-calculator__result-note">{result.notes.join(" ")}</div>
              )}
            </div>

            <div className="pd-results-card">
              <h4 className="pd-h4">Standard applied</h4>
              <p className="pd-calculator__meta">{standardSummary}</p>
              {result?.standard_applied?.area_metric && (
                <p className="pd-calculator__result-note">
                  Area basis: {result.standard_applied.area_metric.toUpperCase()}
                  {result.standard_applied.area_metric === "gea" ? " (Gross External Area)" : " (Gross Internal Area)"}.
                </p>
              )}
              {result?.standard_applied?.ptal && (
                <p className="pd-calculator__result-note">
                  PTAL band applied: {result.standard_applied.ptal.label || `PTAL ${result.standard_applied.ptal.ptal}`}
                </p>
              )}
            </div>

            {(result?.design_checks || typeof result?.inputs?.threshold_m2 === "number") && (
              <div className="pd-results-card">
                <h4 className="pd-h4">Additional information</h4>
                <ul className="pd-notes-list">
                  {typeof result?.inputs?.threshold_m2 === "number" && (
                    <li>
                      Threshold: {result.inputs.threshold_m2.toLocaleString?.() || result.inputs.threshold_m2} m²
                      {result?.standard_applied?.threshold_applied === false ? " — project below threshold." : ""}
                    </li>
                  )}
                  {"accessible_percent_min" in (result?.design_checks || {}) && (
                    <li>Accessible cycle spaces ≥ {result.design_checks.accessible_percent_min}% of total.</li>
                  )}
                  {"cargo_or_adapted_percent" in (result?.design_checks || {}) && (
                    <li>Provide {result.design_checks.cargo_or_adapted_percent}% for cargo/adapted cycles.</li>
                  )}
                </ul>
              </div>
            )}

            {Array.isArray(result?.authority?.sources) && result.authority.sources.length > 0 && (
              <div className="pd-results-card">
                <h4 className="pd-h4">Source</h4>
                <p className="pd-calculator__result-note">
                  {result.authority.sources.map((s, i) => (
                    <span key={s.url}>
                      <a href={s.url} target="_blank" rel="noreferrer">
                        {s.title}
                      </a>
                      {i < result.authority.sources.length - 1 ? " · " : ""}
                    </span>
                  ))}
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="pd-results-placeholder">Run the calculator to see cycle parking outputs.</div>
        )}
      </CalculatorSection>
    </CalculatorLayout>
  );
}

function Stat({ label, value }) {
  let display = value;
  if (display === null || display === undefined) {
    display = "—";
  } else if (typeof display === "number") {
    display = Number.isFinite(display) ? display.toLocaleString() : "—";
  }

  return (
    <div className="pd-stat">
      <div className="pd-stat__label">{label}</div>
      <div className="pd-stat__value">{display}</div>
    </div>
  );
}

function formatArea(value) {
  if (value === null || value === undefined) return "—";
  const num = Number(value);
  if (!Number.isNaN(num) && Number.isFinite(num)) {
    return `${num.toLocaleString()} m²`;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    return value;
  }
  return "—";
}
